/*
 * What the card's two gunbarrel viewers last wrote, read back when the tab
 * mounts.
 *
 * Both viewers already save themselves to the session on every settings
 * change, exactly as a dashboard widget does - nothing here writes. The card
 * only mounts the gunbarrel tab while it is the one showing, so a trip to
 * another tab takes the viewers and their settings stores down with it, and
 * without this they come back from their seeds every time.
 */
import {
  type ThreeDViewerPersistedStateT,
  threeDSessionKey,
  xdaSessionKey
} from "models/vis";

import type { State as XdaProviderState } from "components/vis/context/types";

import {
  WELL_CARD_PANEL_THREE_D_ID,
  WELL_CARD_PANEL_XDA_ID,
  WELL_CARD_PANEL_XDA_TYPE,
  WELL_CARD_XDA_STATE
} from "../constants";

/**
 * The value stored under `key`, or null if there is nothing usable there.
 *
 * Both the reach for storage and the parse are guarded: a private window can
 * throw on `sessionStorage` itself, and the text can be truncated or left over
 * from a shape the viewer no longer writes. Either way the caller's fallback is
 * a better answer than a tab that fails to mount.
 */
function readSessionJson<T>(key: string): Maybe<T> {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/**
 * The cross-section settings the user left behind, or the card's own seed.
 *
 * The shape is checked rather than trusted, because `XdaSettingsStoreProvider`
 * reads nothing but `xda.settings` from a seed: hand it an object without that
 * and it quietly drops back to bare factory defaults, which are not what the
 * card opens with. A payload that parses is therefore still only worth using
 * once that object is genuinely in it.
 */
export function readXdaSessionState(): XdaProviderState {
  const stored = readSessionJson<XdaProviderState>(
    xdaSessionKey(WELL_CARD_PANEL_XDA_ID, WELL_CARD_PANEL_XDA_TYPE)
  );
  return stored?.xda?.settings ? stored : WELL_CARD_XDA_STATE;
}

/**
 * The 3D scene's options as the user left them, or nothing.
 *
 * No shape check here, unlike the section above: the viewer reads this field by
 * field with a default behind each one, so a partial or older payload restores
 * what it does carry and takes the viewer's defaults for the rest.
 */
export function readThreeDSessionState(): Maybe<ThreeDViewerPersistedStateT> {
  return readSessionJson<ThreeDViewerPersistedStateT>(
    threeDSessionKey(WELL_CARD_PANEL_THREE_D_ID)
  );
}
